import React from 'react';

import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { useHandleError } from 'api/hooks/errorHandling';

type Props = {
    children: React.ReactNode;
    retry?: boolean;
    /** Optional override */
    showDevtools?: boolean;
};

type PendingError = { error: unknown; message: string };

// The error handling hook needs access to the notistack context and the react-router context,
// i.e.it needs to be rendered inside the Router and SnackbarProvider components
export function ReactQueryWrapper({ children, retry, showDevtools }: Props) {
    const [pendingErrors, setPendingErrors] = React.useState<PendingError[]>([]);

    // Ensure queryClient is only created once
    const [queryClient] = React.useState(
        () =>
            new QueryClient({
                defaultOptions: {
                    mutations: {
                        retry,
                    },
                    queries: {
                        retry,
                    },
                },
                mutationCache: new MutationCache({
                    onError: (error, _variables, _context, mutation) => {
                        const message = (mutation.meta?.errorMessage as string) || 'Error saving data';
                        setPendingErrors(q => [...q, { error, message }]);
                    },
                }),
                queryCache: new QueryCache({
                    onError: (error, query) => {
                        const message = (query.meta?.errorMessage as string) || 'Error loading data';
                        setPendingErrors(q => [...q, { error, message }]);
                    },
                }),
            })
    );

    const handleError = useHandleError(queryClient);

    React.useEffect(() => {
        if (pendingErrors.length === 0) return;

        const [{ error, message }, ...rest] = pendingErrors;
        handleError(error, message);
        setPendingErrors(rest);
    }, [pendingErrors, handleError]);

    const isCypress = typeof window !== 'undefined' && (window as any).Cypress;
    const shouldShowDevtools = showDevtools ?? (import.meta.env.DEV && !isCypress);

    return (
        <QueryClientProvider client={queryClient}>
            {shouldShowDevtools && <ReactQueryDevtools initialIsOpen={false} />}
            {children}
        </QueryClientProvider>
    );
}
